/**
 * mountReactApp utility module.
 * Mounts a React application into a shadow DOM host
 * with conditionally-composed providers and session bootstrap logic.
 */
// External packages
import { StrictMode, useEffect } from "react";
import ReactDOM from "react-dom/client";

// Internal shared modules
import useBootstrapSession from "../../hooks/session/useBootstrapSession";
import RepStatusBar from "../../components/Rep/RepStatusBar";

// Error tracking
import { Sentry } from "../errorTracking";

// Relative imports - Utils
import createShadowHost from "./createShadowHost";
import {
  applyWecomBodyBackground,
  removeWecomBodyBackground,
} from "./applyBodyBackground";
import { PROVIDER_PRESETS, buildProviderTree } from "./providerPresets";

// Re-export for consumers (e.g. index.js, tests)
export { PROVIDER_PRESETS };

/**
 * Root component wrapper to ensure session bootstrap runs for every mounted app.
 *
 * @component
 * @param {Object} props - Component props.
 * @param {React.ComponentType} props.App - The main App component to render.
 * @param {boolean} [props.skipBootstrap=false] - Skip session bootstrap (e.g. for rep-landing, which authorizes first).
 * @returns {JSX.Element} The rendered root component.
 */
function Root({ App, skipBootstrap = false }) {
  useBootstrapSession({ skip: skipBootstrap });

  // Apply Wecom body background when component mounts
  useEffect(() => {
    applyWecomBodyBackground();

    // Cleanup: remove background when component unmounts
    return () => {
      removeWecomBodyBackground();
    };
  }, []);

  return <App />;
}

/**
 * Mounts a React app into the shadow-host with conditionally-composed providers
 * and session bootstrap.
 *
 * @param {string} rootId - The ID of the shadow host container element.
 * @param {React.ComponentType} AppComponent - The main App component to render.
 * @param {Object} [options] - Mount options.
 * @param {boolean} [options.skipBootstrap=false] - Skip session bootstrap (for flows that authorize first).
 * @param {string} [options.providers="registration"] - Provider preset name (see PROVIDER_PRESETS).
 */
export default function mountReactApp(rootId, AppComponent, options = {}) {
  const { skipBootstrap = false, providers = PROVIDER_PRESETS.registration } = options;
  // Pass CSS URLs array (code-split chunks) to shadow host
  const shadow = createShadowHost(rootId, window.WecomAssets?.css);
  if (!shadow) return;

  const showRepBar = !skipBootstrap;

  // Render the React app with preset-specific providers
  ReactDOM.createRoot(shadow).render(
    <StrictMode>
      <Sentry.ErrorBoundary
        fallback={<div style={{ padding: "1rem", textAlign: "center" }}>Something went wrong. Please refresh the page.</div>}
      >
        {buildProviderTree(providers, (
          <>
            {showRepBar && <RepStatusBar />}
            <Root App={AppComponent} skipBootstrap={skipBootstrap} />
          </>
        ))}
      </Sentry.ErrorBoundary>
    </StrictMode>
  );
}
